<template>
  <div>
    <v-row>
      <v-col>
        <h2 class="mb-3 mt-3 ml-3">
          {{ $t('console.connectedTenantsHome') }}
          <v-btn
            icon="mdi-help-circle-outline"
            @click="showingHomeTenantsListHelpDialog = true"
            rounded="circle"
            width="36"
            height="36"
            elevation="0"
          />
        </h2>

        <v-card elevation=0 class="pa-4">
          <v-row>
            <v-col>
              <TenantsListView
                :tenants="tenantsHome"
                :loading="false"
              >
                <template #topAction>
                  <v-tooltip :text="$t('console.requestConnection')" location="bottom" :disabled="mdAndUp">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        @click="showingRequestConnectionDialog = true"
                        color="primary"
                        elevation="0"
                      >
                        <v-icon class="mr-md-2">mdi-send</v-icon>
                        <span class="d-none d-md-inline">{{ $t('console.requestConnection') }}</span>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </template>
                <template #itemAction="{ tenant }">
                  <v-menu
                    v-model="showHomeActionMenu[tenant.id]"
                    location="left bottom"
                    :persistent="false"
                  >
                    <template v-slot:activator="{ props }">
                      <v-btn size="small" density="comfortable" variant="text" height="28" rounded="circle" icon v-bind="props">
                        <v-icon color="primary" size="24" data-cy="list-tenants-actions-btn">mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>

                    <v-list width="auto" data-cy="list-tenants-actions-list">
                      <v-list-item @click="homeTenantManagePermissions(tenant)">
                        <v-list-item-title>
                          <v-icon size="small" start>mdi-pencil</v-icon>
                          {{ $t('console.managePermissions') }}
                        </v-list-item-title>
                      </v-list-item>

                      <v-list-item @click="tenantDisconnect(tenant)">
                        <v-list-item-title>
                          <v-icon size="small" start>mdi-delete-sweep-outline</v-icon>
                          {{ $t('console.disconnect') }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </TenantsListView>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>



    <v-row>
      <v-col>
        <h2 class="mb-3 mt-3 ml-3">
          {{ $t('console.connectedTenantsAway') }}
          <v-btn
            icon="mdi-help-circle-outline"
            @click="showingAwayTenantsListHelpDialog = true"
            rounded="circle"
            width="36"
            height="36"
            elevation="0"
          />
        </h2>

        <v-card elevation=0 class="pa-4">
          <v-row>
            <v-col>
              <TenantsListView
              :tenants="tenantsAway"
              :loading="false"
              >
                <template #topAction>
                  <v-tooltip :text="$t('console.connectTenant')" location="bottom" :disabled="mdAndUp">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        @click="showingConnectTenantDialog = true"
                        color="primary"
                        elevation="0"
                      >
                        <v-icon class="mr-md-2">mdi-connection</v-icon>
                        <span class="d-none d-md-inline">{{ $t('console.connectTenant') }}</span>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </template>

                <template #itemAction="{ tenant }">
                  <v-menu
                    v-model="showAwayActionMenu[tenant.id]"
                    location="left bottom"
                    :persistent="false"
                  >
                    <template v-slot:activator="{ props }">
                      <v-btn size="small" density="comfortable" variant="text" height="28" rounded="circle" icon v-bind="props">
                        <v-icon color="primary" size="24" data-cy="list-tenants-actions-btn">mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>

                    <v-list data-cy="list-tenants-actions-list">
                      <v-list-item @click="awayTenantManageGroups(tenant)">
                        <v-list-item-title>
                          <v-icon size="small" start>mdi-select-group</v-icon>
                          {{ $t('console.manageGroups') }}
                        </v-list-item-title>
                      </v-list-item>

                      <v-list-item @click="awayTenantManageUsers(tenant)">
                        <v-list-item-title>
                          <v-icon size="small" start>mdi-account-group</v-icon>
                          {{ $t('console.manageUsers') }}
                        </v-list-item-title>
                      </v-list-item>

                      <v-list-item @click="tenantDisconnect(tenant)">
                        <v-list-item-title>
                          <v-icon size="small" start>mdi-delete-sweep-outline</v-icon>
                          {{ $t('console.disconnect') }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </TenantsListView>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <HomeTenantCreateDialog
      v-model="showingRequestConnectionDialog"
    />

    <HomeTenantPermissionsDialog
      v-model="showingManageTenantPermissionsDialog"
      :tenantConnection="selectedTenantConnection"
    />

    <AwayTenantCreateDialog
      v-model="showingConnectTenantDialog"
    />

    <AwayTenantGroupsDialog
      v-model="showingAwayTenantGroupsDialog"
      :tenantConnection="selectedTenantConnection"
    />

    <AwayTenantUsersDialog
      v-model="showingAwayTenantUsersDialog"
      :tenantConnection="selectedTenantConnection"
    />

    <DisconnectTenantDialog
      v-model="showDisconnectTenantDialog"
      :tenantConnection="selectedTenantConnection"
    />

    <HomeTenantListHelpDialog
      v-model="showingHomeTenantsListHelpDialog"
    />

    <AwayTenantListHelpDialog
      v-model="showingAwayTenantsListHelpDialog"
    />
  </div>
</template>

<script>
import { useDisplay } from 'vuetify';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useTenantConnectionsStore } from '@/stores/TenantConnectionsStore.js';
import TenantsListView from './components/TenantsListView.vue';
import HomeTenantCreateDialog from './components/dialogs/HomeTenantCreateDialog.vue';
import HomeTenantPermissionsDialog from './components/dialogs/HomeTenantPermissionsDialog.vue';
import AwayTenantCreateDialog from './components/dialogs/AwayTenantCreateDialog.vue';
import AwayTenantGroupsDialog from './components/dialogs/AwayTenantGroupsDialog.vue';
import AwayTenantUsersDialog from './components/dialogs/AwayTenantUsersDialog.vue';
import DisconnectTenantDialog from './components/dialogs/DisconnectTenantDialog.vue';
import HomeTenantListHelpDialog
  from '@/routes/console/tenant-connections/components/dialogs/HomeTenantListHelpDialog.vue';
import AwayTenantListHelpDialog
  from '@/routes/console/tenant-connections/components/dialogs/AwayTenantListHelpDialog.vue';

export default {
  name: "TenantConnections",
  components: {
    AwayTenantListHelpDialog,
    HomeTenantListHelpDialog,
    TenantsListView,
    HomeTenantCreateDialog,
    HomeTenantPermissionsDialog,
    AwayTenantCreateDialog,
    AwayTenantGroupsDialog,
    AwayTenantUsersDialog,
    DisconnectTenantDialog,
  },
  setup() {
    const display = useDisplay();
    return { mdAndUp: display.mdAndUp };
  },
  data() {
    return {

      showingRequestConnectionDialog: false,
      showingConnectTenantDialog: false,
      showingManageTenantPermissionsDialog: false,
      showDisconnectTenantDialog: false,
      showingAwayTenantGroupsDialog: false,
      showingAwayTenantUsersDialog: false,
      showingHomeTenantsListHelpDialog: false,
      showingAwayTenantsListHelpDialog: false,

      selectedTenantConnection: null,

      // tenantsHome: [
      //   {id: 1, name: 'Tenant A', users: 12, node: {id: 111, name: 'Folder 1'}, },
      //   {id: 2, name: 'Tenant B', users: 24, node: {id: 111, name: 'Folder 2'}, },
      // ],
      // tenantsAway: [
      //   {id: 1, name: 'Tenant A', users: 12, node: {id: 111, name: 'Folder 1'}, },
      //   {id: 2, name: 'Tenant B', users: 24, node: {id: 111, name: 'Folder 2'}, },
      // ],

      showHomeActionMenu: {},
      showAwayActionMenu: {},
    };
  },
  async mounted() {
    if (!this.user) {
      const authStore = useAuthStore();
      await authStore.refreshDBUser();
    }

    if (!this.$canControlTenantConfig()) {
      this.$router.push('/console/index');
      return;
    }

    if (this.rootNodeId) {
      // console.log("Root node ID");
      const tenantConnectionsStore = useTenantConnectionsStore();
      tenantConnectionsStore.refreshHomeTenantsList(this.rootNodeId);
      tenantConnectionsStore.refreshAwayTenantsList(this.rootNodeId);
    }
  },
  methods: {
    homeTenantManagePermissions(tenant) {
      this.selectedTenantConnection = tenant;
      this.showingManageTenantPermissionsDialog = true;
    },
    awayTenantManageGroups(tenant) {
      this.selectedTenantConnection = tenant;
      this.showingAwayTenantGroupsDialog = true;
    },
    awayTenantManageUsers(tenant) {
      this.selectedTenantConnection = tenant;
      this.showingAwayTenantUsersDialog = true;
    },
    tenantDisconnect(tenant) {
      this.selectedTenantConnection = tenant;
      this.showDisconnectTenantDialog = true;
    },
  },
  computed: {
    rootNodeId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    tenantsHome() {
      const tenantConnectionsStore = useTenantConnectionsStore();
      return tenantConnectionsStore.homeTenantsList;
    },
    tenantsAway() {
      const tenantConnectionsStore = useTenantConnectionsStore();
      return tenantConnectionsStore.awayTenantsList;
    },
  }
};
</script>

<style>
</style>
